import React, { JSX, useState } from 'react';
import {
    Create,
    useCreate,
    SimpleForm,
    NumberInput,
    SelectInput,
    useNotify,
    useRefresh,
    BooleanInput,
    required,
    TextInput,
    Edit, ReferenceArrayInput, AutocompleteArrayInput, useRecordContext,
    Toolbar, SaveButton
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Box,
    Stack,
    LinearProgress,
    Tooltip,
    Typography
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { CustomerAccount } from "../../../../types/entities";
import { useYearChoices } from "../../../../defaults/defaults";
import { useGetIndustries } from "../../../global-hooks/default-system-hooks";
import { ChatFieldBridge } from "../../../chat-bot/components/ChatFieldBridge";
import { useCustomerAccountAllowances } from "./CustomerAccountAllowanceProvider";

/**
 * Save, and nothing else.
 *
 * The form below sits inside an `<Edit>` on the customer account, so react-admin's
 * default toolbar put a delete button for **the account** on the screen for adding a
 * company to it. Deleting the account belongs on the account's own screen. HQ-28.
 */
const SaveOnlyToolbar: () => JSX.Element = (): JSX.Element => {
    return (
        <Toolbar>
            <SaveButton />
        </Toolbar>
    );
}

const SeatHolderArrayOptionText: () => JSX.Element = (): JSX.Element => {
    const record: any = useRecordContext();
    return (
        <span>
            { `(#${ record.id }) ${ record.name }` }
        </span>
    );
}

export const CustomerAccountNewCompanyModal: (props: any) => JSX.Element = (props: any): JSX.Element => {
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [tabNumber, setTabNumber] = useState(0);
    const [create] = useCreate();
    const notify: any = useNotify();
    const refresh: any = useRefresh();
    const record: CustomerAccount = props.record;
    const yearChoices: { id: string, name: string }[] = useYearChoices();
    const { loadedIndustries, loadingIndustries } = useGetIndustries();
    const { loadingAllowances, allowances } = useCustomerAccountAllowances();

    // Both tabs inside spend the company pool - one creates a company against the account,
    // the other attaches companies that already exist - so the gate belongs on the way in
    // rather than on either tab. HQ-API refuses both regardless; this is so the admin is
    // told before filling a form that cannot be saved.
    const noCompanyHeadroom: boolean = allowances?.companies?.headroom === false;

    function handleTabNumberChange(number: number): void {
        setTabNumber(number);
    }

    function handleClose(): void {
        setOpenModal(false);
    }

    function handleOpen(): void {
        setOpenModal(true);
    }

    function handleSubmit(values: any): void {
        if (record) {
            values.customerAccount = record.id
            create(
                'companies',
                {
                    data: values
                },
                {
                    onSuccess: async (data: any): Promise<void> => {
                        refresh();
                        notify("New Company created successfully!", { type: "success" });
                        handleClose();
                    },
                    onError: (error: any): void => {
                        let errorMessage: string;
                        if (typeof error === 'string') {
                            errorMessage = error;
                        } else {
                            errorMessage = error.message;
                        }

                        notify(`Failed to create error: ${errorMessage}`, { type: 'error' });
                    },
                }
            )
        }
    }

    const inputText = choice => `(#${choice.id}) ${choice.name}`;

    return (
        <Box sx={ { width: '100%', margin: '20px 0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' } }>
            { noCompanyHeadroom &&
                <Typography sx={ { fontSize: '13px', color: 'var(--wf-bad)', textAlign: 'right' } }>
                    { `This account has used all ${ allowances?.companies?.allowed ?? 0 } of the companies its `
                        + `subscription allows. Raise the companies bolt-on on the Subscription tab to add another.` }
                </Typography>
            }
            <Tooltip title={ noCompanyHeadroom ? 'No companies left on this plan' : '' }>
                <span>
                    <Button variant='contained'
                            onClick={ handleOpen }
                            disabled={ loadingAllowances || noCompanyHeadroom } >
                        { "Add New Company" }
                        <AddIcon />
                    </Button>
                </span>
            </Tooltip>
            <Dialog
                maxWidth="lg"
                fullWidth
                open={ openModal }
                onClose={ handleClose }>
                <DialogTitle>
                    <div style={ { width: '100%', display: 'flex', justifyContent: 'space-between' } }>
                        <span>{ "Add Company To Customer Account" }</span>
                        <button type="button"
                                onClick={ handleClose }
                                style={ { cursor: 'pointer' } }>
                            <CloseIcon />
                        </button>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={ 2 } >
                        <Stack direction="row" spacing={ 1 }>
                            <Button variant='contained'
                                    sx={ {
                                        width: '50%',
                                    } }
                                    color={ (tabNumber === 0 ? 'success' : 'primary') }
                                    onClick={ (): void => handleTabNumberChange(0) }
                            >
                                { "Add New Company" }
                            </Button>
                            <Button variant="contained"
                                    sx={ {
                                        width: '50%',
                                    } }
                                    color={ (tabNumber === 1 ? 'success' : 'primary') }
                                    onClick={ (): void => handleTabNumberChange(1) }
                            >
                                { "Create and Add Company" }
                            </Button>
                        </Stack>
                        <Box>
                            { (tabNumber === 0) &&
                                <Box>
                                    <Edit record={ record.id }
                                          redirect={ false }
                                          title=" "
                                    >
                                        <SimpleForm toolbar={ <SaveOnlyToolbar /> }>
                                            <ReferenceArrayInput name="companies" source="companies" reference="companies" >
                                                <AutocompleteArrayInput
                                                    optionText={ <SeatHolderArrayOptionText /> }
                                                    inputText={ inputText }
                                                    // shouldRenderSuggestions={ (val: any): boolean => { return val.trim().length > 2 }}
                                                />
                                            </ReferenceArrayInput>
                                            { /* Offers this modal to the assistant. Renders nothing, writes nothing by
                                                 itself. HQ-12: the registry is a stack now, so this no longer displaces
                                                 the page form underneath. */ }
                                            <ChatFieldBridge />
                                        </SimpleForm>
                                    </Edit>

                                </Box>
                            }
                            { (tabNumber === 1) &&
                                <Create resource="companies" title=" ">
                                    <SimpleForm onSubmit={ handleSubmit }>
                                        <TextInput name="name" source="name" validate={[required()]} fullWidth />
                                        <SelectInput
                                            name="yearEstablished"
                                            source="yearEstablished"
                                            choices={ yearChoices }
                                            optionValue="id"
                                        />
                                        <TextInput name="vat" source="vat" fullWidth />
                                        <TextInput name="sicCode" source="sicCode" fullWidth />
                                        { (loadingIndustries) &&
                                            <LinearProgress sx={ { margin: '10px 0', width: '50%' } } />
                                        }
                                        { (!loadingIndustries) &&
                                            <SelectInput
                                                name="industry"
                                                source="industry"
                                                choices={ loadedIndustries }
                                                optionValue="id"
                                            />
                                        }

                                        <TextInput name="companyNumber" source="companyNumber" fullWidth />
                                        { /* Offers this modal to the assistant. Renders nothing, writes nothing by
                                             itself. HQ-12: the registry is a stack now, so this no longer displaces
                                             the page form underneath. */ }
                                        <ChatFieldBridge />
                                    </SimpleForm>
                                </Create>
                            }
                        </Box>
                    </Stack>

                </DialogContent>
            </Dialog>
        </Box>
    );
}